import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ToasterSeverities } from "@core/enums/toaster-severities";
import { DialogHelper } from "@core/helpers/dialog-helper";
import {
  EmployeeApiResponse,
  EmployeeSaveResponse,
  EmployeesService,
} from "@core/services/employees.service";
import { NotificationService } from "@core/services/notification.service";
import * as employeesActions from "@features/employees/store/employees.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, finalize, map, mergeMap, of, tap } from "rxjs";

interface BackendLogicalError {
  code?: number | string;
  message?: string;
  detail?: string;
  success?: boolean;
}

@Injectable()
export class EmployeesEffects {
  loadEmployeesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(employeesActions.LOAD_EMPLOYEES),
      tap(() => {
        this.store.dispatch(
          employeesActions.SET_EMPLOYEES_LOADING({ loading: true })
        );
      }),
      mergeMap(() =>
        this.employeeService.getEmployees$().pipe(
          map((employeesList) =>
            employeesActions.SET_EMPLOYEES_LIST({ employeesList })
          ),
          catchError((error: HttpErrorResponse) =>
            of(employeesActions.EMPLOYEES_ACTION_FAILURE({ error }))
          ),
          finalize(() => {
            this.store.dispatch(
              employeesActions.SET_EMPLOYEES_LOADING({ loading: false })
            );
          })
        )
      )
    )
  );

  saveEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(employeesActions.SAVE_EMPLOYEE),

      tap(() => {
        this.store.dispatch(
          employeesActions.SET_EMPLOYEES_LOADING({ loading: true })
        );
      }),

      mergeMap((action) =>
        this.employeeService.saveEmployee$(action.data).pipe(
          mergeMap((response) => {
            if (this.isLogicalFailure(response)) {
              const error = new HttpErrorResponse({
                status: this.isDuplicateEmailResponse(response) ? 409 : 400,
                error: response,
              });

              this.showSaveError(error);

              return of(
                employeesActions.EMPLOYEES_ACTION_FAILURE({
                  error,
                })
              );
            }

            this.dialog.closeAll();

            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_SAVED_SUCCESSFULLY"
            );

            return of(employeesActions.LOAD_EMPLOYEES());
          }),

          catchError((error: HttpErrorResponse) => {
            this.showSaveError(error);

            return of(
              employeesActions.EMPLOYEES_ACTION_FAILURE({
                error,
              })
            );
          }),

          finalize(() => {
            this.store.dispatch(
              employeesActions.SET_EMPLOYEES_LOADING({ loading: false })
            );
          })
        )
      )
    )
  );

  updateEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(employeesActions.UPDATE_EMPLOYEE),

      tap(() => {
        this.store.dispatch(
          employeesActions.SET_EMPLOYEES_LOADING({ loading: true })
        );
      }),

      mergeMap((action) =>
        this.employeeService.updateEmployee$(action).pipe(
          map(() => {
            this.dialog.closeAll();

            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_SAVED_SUCCESSFULLY"
            );

            return employeesActions.LOAD_EMPLOYEES();
          }),

          catchError((error: HttpErrorResponse) => {
            this.showSaveError(error);

            return of(
              employeesActions.EMPLOYEES_ACTION_FAILURE({
                error,
              })
            );
          }),

          finalize(() => {
            this.store.dispatch(
              employeesActions.SET_EMPLOYEES_LOADING({ loading: false })
            );
          })
        )
      )
    )
  );

  activateEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(employeesActions.ACTIVATE_EMPLOYEE),

      tap(() => {
        this.store.dispatch(
          employeesActions.SET_EMPLOYEES_LOADING({ loading: true })
        );
      }),

      mergeMap((action) =>
        this.employeeService.activateEmployee$(action.id).pipe(
          map(() => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "EMPLOYEE_ACTIVATED_SUCCESSFULLY"
            );

            return employeesActions.LOAD_EMPLOYEES();
          }),

          catchError((error: HttpErrorResponse) => {
            this.showActionError(error);

            return of(employeesActions.EMPLOYEES_ACTION_FAILURE({ error }));
          }),

          finalize(() => {
            this.store.dispatch(
              employeesActions.SET_EMPLOYEES_LOADING({ loading: false })
            );
          })
        )
      )
    )
  );

  deactivateEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(employeesActions.DEACTIVATE_EMPLOYEE),

      tap(() => {
        this.store.dispatch(
          employeesActions.SET_EMPLOYEES_LOADING({ loading: true })
        );
      }),

      mergeMap((action) =>
        this.employeeService.deactivateEmployee$(action.id).pipe(
          map(() => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "EMPLOYEE_DEACTIVATED_SUCCESSFULLY"
            );

            return employeesActions.LOAD_EMPLOYEES();
          }),

          catchError((error: HttpErrorResponse) => {
            this.showActionError(error);

            return of(employeesActions.EMPLOYEES_ACTION_FAILURE({ error }));
          }),

          finalize(() => {
            this.store.dispatch(
              employeesActions.SET_EMPLOYEES_LOADING({ loading: false })
            );
          })
        )
      )
    )
  );

  resendConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(employeesActions.RESEND_CONFIRMATION),

      tap(() => {
        this.store.dispatch(
          employeesActions.SET_EMPLOYEES_LOADING({ loading: true })
        );
      }),

      mergeMap((action) =>
        this.employeeService.resendConfirmation$(action.id).pipe(
          map(() => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "CONFIRMATION_EMAIL_SENT"
            );

            return employeesActions.LOAD_EMPLOYEES();
          }),

          catchError((error: HttpErrorResponse) => {
            this.showActionError(error);

            return of(employeesActions.EMPLOYEES_ACTION_FAILURE({ error }));
          }),

          finalize(() => {
            this.store.dispatch(
              employeesActions.SET_EMPLOYEES_LOADING({ loading: false })
            );
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private employeeService: EmployeesService,
    private store: Store,
    private dialog: DialogHelper
  ) {}

  private showActionError(error: HttpErrorResponse): void {
    this.notificationService.show(
      ToasterSeverities.Error,
      "EMPLOYEE_ACTION_FAILED",
      this.getBackendErrorMessage(error)
    );
  }

  private isLogicalFailure(response: EmployeeApiResponse): boolean {
    if (response === null || response === undefined) {
      return true;
    }

    if (typeof response === "number") {
      return response >= 400;
    }

    if (typeof response === "string") {
      const value = response.trim().toLowerCase();

      if (!value) {
        return true;
      }

      const numericStatus = Number(value);

      if (!Number.isNaN(numericStatus) && numericStatus >= 400) {
        return true;
      }

      return (
        value.includes("error") ||
        value.includes("exception") ||
        value.includes("failed") ||
        value.includes("invalid") ||
        value.includes("already exists") ||
        value.includes("already associated")
      );
    }

    const status = Number(
      response.statusCode ?? response.status ?? response.code
    );

    if (!Number.isNaN(status) && status >= 400) {
      return true;
    }

    return (
      response.success === false || this.containsBackendErrorText(response)
    );
  }

  private isDuplicateEmailError(error: BackendLogicalError): boolean {
    const text = `${error.code ?? ""} ${error.message ?? ""} ${
      error.detail ?? ""
    }`.toLowerCase();

    return (
      text.includes("email") &&
      (text.includes("already") ||
        text.includes("existing") ||
        text.includes("associated") ||
        text.includes("duplicate"))
    );
  }

  private showSaveError(error: HttpErrorResponse): void {
    const backendError = this.asBackendError(error.error);

    if (error.status === 409 || this.isDuplicateEmailError(backendError)) {
      this.notificationService.show(
        ToasterSeverities.Error,
        "EMPLOYEE_EMAIL_ALREADY_EXISTS"
      );

      return;
    }

    this.notificationService.show(
      ToasterSeverities.Error,
      "DATA_COULD_NOT_BE_SAVED",
      this.getBackendErrorMessage(error)
    );
  }

  private asBackendError(value: unknown): BackendLogicalError {
    return value && typeof value === "object"
      ? (value as BackendLogicalError)
      : { message: typeof value === "string" ? value : "" };
  }

  private getBackendErrorMessage(error: HttpErrorResponse): string {
    if (typeof error.error === "string") {
      return error.error;
    }

    return error.error?.message ?? error.error?.detail ?? error.message ?? "";
  }

  private isDuplicateEmailResponse(response: EmployeeApiResponse): boolean {
    if (typeof response === "number" || response === null) {
      return false;
    }

    const text =
      typeof response === "string"
        ? response.toLowerCase()
        : [
            response.code,
            response.status,
            response.statusCode,
            response.message,
            response.detail,
          ]
            .filter(
              (value): value is string | number =>
                value !== undefined && value !== null
            )
            .join(" ")
            .toLowerCase();

    return (
      text.includes("email") &&
      (text.includes("already") ||
        text.includes("existing") ||
        text.includes("associated") ||
        text.includes("duplicate"))
    );
  }

  private containsBackendErrorText(response: EmployeeSaveResponse): boolean {
    const text = [response.message, response.detail]
      .filter((value): value is string => !!value)
      .join(" ")
      .toLowerCase();

    return (
      text.includes("error") ||
      text.includes("exception") ||
      text.includes("failed") ||
      text.includes("invalid") ||
      text.includes("already exists") ||
      text.includes("already associated") ||
      text.includes("duplicate")
    );
  }
}
